import React, { useState, useEffect, useRef } from 'react';

const PLANS = [
  {
    name: 'Starter',
    pkr: 2000,
    color: '#00c853',
    icon: '⚡',
    description: 'Perfect for beginners who want fully automatic publishing',
    features: [
      '✅ Auto upload to YouTube and Facebook',
      '✅ Zero human intervention publishing',
      '✅ Basic Obsidian AI metadata (50 generations/month)',
      '✅ 1 channel per platform',
      '✅ Scheduled publishing',
      '❌ AI title and description selection',
      '❌ Thumbnail generation',
      '❌ ObsidianEye video scorer',
      '❌ GuardianAI',
      '❌ ContentForge'
    ]
  },
  {
    name: 'Creator',
    pkr: 4500,
    color: '#00bcd4',
    icon: '🎨',
    description: 'For content creators who want AI powered metadata',
    popular: true,
    features: [
      '✅ Everything in Starter',
      '✅ AI generated titles for all platforms',
      '✅ AI generated descriptions and captions',
      '✅ AI generated keywords and hashtags',
      '✅ 5 AI options to choose from per upload',
      '✅ 300 Obsidian AI generations/month',
      '✅ 3 channels per platform',
      '✅ Niche auto detection',
      '❌ Thumbnail generation',
      '❌ ObsidianEye video scorer',
      '❌ GuardianAI',
      '❌ ContentForge'
    ]
  },
  {
    name: 'Pro',
    pkr: 8000,
    color: '#7c4dff',
    icon: '🚀',
    description: 'For serious creators who want every edge',
    features: [
      '✅ Everything in Creator',
      '✅ AI thumbnail generation',
      '✅ ObsidianEye video scorer',
      '✅ CTR and SEO score per option',
      '✅ 800 Obsidian AI generations/month',
      '✅ 5 channels per platform',
      '✅ TikTok and Instagram publishing',
      '✅ ContentForge script and production pipeline',
      '❌ GuardianAI content policy checker',
      '❌ Unlimited channels'
    ]
  },
  {
    name: 'Agency',
    pkr: 15000,
    color: '#ff6d00',
    icon: '🏢',
    description: 'For agencies managing multiple creators',
    features: [
      '✅ Everything in Pro',
      '✅ GuardianAI content policy checker',
      '✅ Unlimited channels',
      '✅ 2000 Obsidian AI generations/month',
      '✅ Multiple client workspaces',
      '✅ Priority AI processing',
      '✅ ChannelForge growth guide',
      '✅ Advanced analytics',
      '✅ Bulk video scheduling',
      '✅ Email support'
    ]
  },
  {
    name: 'Enterprise',
    pkr: 25000,
    color: '#f50057',
    icon: '👑',
    description: 'Full power for large teams and businesses',
    features: [
      '✅ Everything in Agency',
      '✅ Unlimited Obsidian AI generations',
      '✅ White label option',
      '✅ Dedicated account manager',
      '✅ Custom integrations',
      '✅ API access',
      '✅ Priority 24/7 support',
      '✅ Custom AI training on your niche',
      '✅ Team member accounts',
      '✅ SLA guarantee'
    ]
  }
];

const CURRENCIES = {
  PKR: { symbol: 'Rs', rate: 1 },
  USD: { symbol: '$', rate: 0.0036 },
  GBP: { symbol: '£', rate: 0.0028 },
  EUR: { symbol: '€', rate: 0.0033 },
  AED: { symbol: 'AED', rate: 0.013 },
  SAR: { symbol: 'SAR', rate: 0.013 }
};

const FEATURES = [
  { icon: '🤖', title: 'Obsidian AI', desc: 'Our proprietary AI generates platform specific titles, descriptions, keywords and hashtags optimised for each platform algorithm separately' },
  { icon: '⚡', title: 'Zero Human Intervention', desc: 'Upload once and walk away. Vantage automatically publishes to YouTube, Facebook, TikTok and Instagram at your scheduled time' },
  { icon: '🎯', title: 'Platform Specific Content', desc: 'YouTube SEO titles, TikTok viral captions, Facebook community descriptions and Instagram aesthetic captions — all different and optimised per algorithm' },
  { icon: '🖼️', title: 'AI Thumbnail Studio', desc: 'Obsidian AI generates high CTR thumbnail concepts with psychology analysis then creates the actual image automatically' },
  { icon: '👁️', title: 'ObsidianEye Scorer', desc: 'Score your videos before publishing. Get CTR predictions, SEO strength analysis and improvement suggestions powered by AI' },
  { icon: '🛡️', title: 'GuardianAI', desc: 'Check your content against platform policies before publishing. Avoid strikes, bans and demonetisation automatically' },
  { icon: '📝', title: 'ContentForge', desc: '5 stage AI content pipeline from script writing to production blueprint to thumbnail to CTR optimised titles — everything in one place' },
  { icon: '📊', title: 'Smart Analytics', desc: 'Track performance across all platforms in one dashboard. See what works and double down on winning content strategies' }
];

export default function LandingPage({ onLogin, onSignup, onPrivacy, onTerms, onReturn }) {
  const [currency, setCurrency] = useState('PKR');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pricingRef = useRef(null);
  const featuresRef = useRef(null);
  const contactRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz.includes('Karachi') || tz.includes('Pakistan')) setCurrency('PKR');
    else if (tz.includes('Dubai') || tz.includes('Abu_Dhabi')) setCurrency('AED');
    else if (tz.includes('Riyadh')) setCurrency('SAR');
    else if (tz.includes('London')) setCurrency('GBP');
    else if (tz.includes('Europe')) setCurrency('EUR');
    else setCurrency('USD');
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatPrice = (pkr) => {
    const cur = CURRENCIES[currency];
    const converted = pkr * cur.rate;
    if (currency === 'PKR') return `${cur.symbol} ${converted.toLocaleString()}`;
    return `${cur.symbol}${converted.toFixed(0)}`;
  };

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth' });

  const handleGetStarted = (plan) => {
    setSelectedPlan(plan);
    setShowPayment(true);
  };

  const PlanCard = ({ plan }) => (
    <div className="plan-card" style={{
      padding: '32px 24px', borderRadius: '20px',
      background: plan.popular ? 'rgba(0,200,83,0.06)' : 'rgba(255,255,255,0.02)',
      border: '2px solid ' + (plan.popular ? 'rgba(0,200,83,0.4)' : 'rgba(255,255,255,0.06)'),
      position: 'relative'
    }}>
      {plan.popular && (
        <div style={{
          position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg,#00c853,#1de9b6)',
          color: '#000', fontSize: '11px', fontWeight: '800',
          padding: '4px 16px', borderRadius: '20px', whiteSpace: 'nowrap'
        }}>MOST POPULAR</div>
      )}
      <div style={{ fontSize: '32px', marginBottom: '12px' }}>{plan.icon}</div>
      <h3 style={{ fontSize: '22px', fontWeight: '800', marginBottom: '8px', color: plan.color }}>{plan.name}</h3>
      <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px', lineHeight: '1.5' }}>{plan.description}</p>
      <div style={{ marginBottom: '24px' }}>
        <span style={{ fontSize: '36px', fontWeight: '800', color: '#fff' }}>{formatPrice(plan.pkr)}</span>
        <span style={{ fontSize: '14px', color: '#64748b' }}>/month</span>
      </div>
      <button className="btn-hover" onClick={() => handleGetStarted(plan)} style={{
        width: '100%', padding: '12px', borderRadius: '10px', cursor: 'pointer', marginBottom: '24px',
        background: plan.popular ? 'linear-gradient(135deg,#00c853,#1de9b6)' : `rgba(${plan.color === '#00c853' ? '0,200,83' : plan.color === '#00bcd4' ? '0,188,212' : plan.color === '#7c4dff' ? '124,77,255' : plan.color === '#ff6d00' ? '255,109,0' : '245,0,87'},0.15)`,
        border: plan.popular ? 'none' : `1px solid ${plan.color}40`,
        color: plan.popular ? '#000' : plan.color, fontSize: '14px', fontWeight: '700'
      }}>Get {plan.name} →</button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {plan.features.map((f, fi) => (
          <div key={fi} style={{ fontSize: '12px', color: f.startsWith('✅') ? '#94a3b8' : '#334155', lineHeight: '1.4' }}>{f}</div>
        ))}
      </div>
    </div>
  );

  const FeatureCard = ({ feature }) => (
    <div className="feature-card" style={{ padding: '28px', borderRadius: '16px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ fontSize: '32px', marginBottom: '16px' }}>{feature.icon}</div>
      <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '10px' }}>{feature.title}</h3>
      <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.7' }}>{feature.desc}</p>
    </div>
  );

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans','DM Sans',sans-serif", background: '#080e1a', color: '#fff', minHeight: '100vh', overflowX: 'hidden', width: '100%' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { width: 100%; overflow-x: hidden; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #080e1a; }
        ::-webkit-scrollbar-thumb { background: #00c853; border-radius: 2px; }
        .plan-card:hover { transform: translateY(-8px); box-shadow: 0 20px 40px rgba(0,0,0,0.3); }
        .plan-card { transition: all 0.3s ease; }
        .feature-card:hover { transform: translateY(-4px); background: rgba(0,200,83,0.08) !important; }
        .feature-card { transition: all 0.3s ease; }
        .nav-link:hover { color: #00c853 !important; }
        .nav-link { transition: color 0.2s ease; cursor: pointer; }
        .btn-hover:hover { transform: scale(1.05); box-shadow: 0 8px 30px rgba(0,200,83,0.4); }
        .btn-hover { transition: all 0.2s ease; }
        .btn-secondary-hover:hover { transform: scale(1.05); }
        .btn-secondary-hover { transition: all 0.2s ease; }
        @media (max-width: 900px) {
          .pricing-grid-top { grid-template-columns: repeat(2,1fr) !important; }
          .pricing-grid-bot { grid-template-columns: repeat(2,1fr) !important; }
          .features-row { grid-template-columns: repeat(2,1fr) !important; }
          .features-row-last { grid-template-columns: repeat(2,1fr) !important; width: 100% !important; }
        }
        @media (max-width: 600px) {
          .nav-mid { display: none !important; }
          .nav-login { display: none !important; }
          .hero-h { font-size: 28px !important; line-height: 1.25 !important; letter-spacing: -0.3px !important; }
          .hero-btns { flex-direction: column !important; align-items: center !important; }
          .hero-btns button { width: 100% !important; max-width: 300px; }
          .stats-row { gap: 24px !important; }
          .pricing-grid-top { grid-template-columns: 1fr !important; }
          .pricing-grid-bot { grid-template-columns: 1fr !important; width: 100% !important; }
          .features-row { grid-template-columns: 1fr !important; }
          .features-row-last { grid-template-columns: 1fr !important; width: 100% !important; }
          .contact-grid { grid-template-columns: 1fr !important; }
          .step-row { flex-direction: column !important; gap: 8px !important; }
          .footer-row { flex-direction: column !important; align-items: flex-start !important; gap: 16px !important; }
          .nav-bar { padding: 12px 20px !important; }
          section, .section-wrap { padding-left: 16px !important; padding-right: 16px !important; }
        }
      `}</style>

      {/* NAVBAR */}
      <nav className="nav-bar" style={{
        position: 'fixed', top: 0, left: 0, right: 0, width: '100%', zIndex: 1000,
        padding: '16px 48px',
        background: scrolled ? 'rgba(8,14,26,0.97)' : 'rgba(8,14,26,0.6)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        transition: 'all 0.3s ease',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        {/* Logo — far left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <img src="/logo.png" alt="Vantage" style={{ width: '36px', height: '36px', borderRadius: '9px', objectFit: 'cover' }}
            onError={e => { e.target.style.display = 'none'; }} />
          <span style={{ fontSize: '20px', fontWeight: '800', color: '#fff', letterSpacing: '-0.3px' }}>Vantage</span>
        </div>

        {/* Nav links — center */}
        <div className="nav-mid" style={{ display: 'flex', alignItems: 'center', gap: '32px', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          <span className="nav-link" onClick={() => scrollTo(featuresRef)} style={{ fontSize: '14px', fontWeight: '500', color: '#94a3b8' }}>Features</span>
          <span className="nav-link" onClick={() => scrollTo(pricingRef)} style={{ fontSize: '14px', fontWeight: '500', color: '#94a3b8' }}>Pricing</span>
          <span className="nav-link" onClick={() => scrollTo(contactRef)} style={{ fontSize: '14px', fontWeight: '500', color: '#94a3b8' }}>Contact</span>
        </div>

        {/* Buttons — far right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <button className="nav-login btn-secondary-hover" onClick={onLogin} style={{
            padding: '8px 20px', borderRadius: '8px', cursor: 'pointer',
            background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit'
          }}>Login</button>
          <button className="btn-hover" onClick={onSignup} style={{
            padding: '8px 20px', borderRadius: '8px', cursor: 'pointer',
            background: 'linear-gradient(135deg,#00c853,#1de9b6)',
            border: 'none', color: '#000', fontSize: '14px', fontWeight: '700', fontFamily: 'inherit'
          }}>Get Started</button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '120px 40px 80px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: '700px', height: '600px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,200,83,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: '860px', position: 'relative', zIndex: 1, width: '100%' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '20px', marginBottom: '24px', background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.3)', fontSize: '13px', fontWeight: '600', color: '#00c853' }}>
            🚀 The Future of Content Publishing is Here
          </div>
          <h1 className="hero-h" style={{ fontSize: 'clamp(28px,3.5vw,52px)', fontWeight: '800', lineHeight: '1.18', letterSpacing: '-0.5px', marginBottom: '24px' }}>
            Upload Once.{' '}
            <span style={{ background: 'linear-gradient(135deg,#00c853,#1de9b6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Publish Everywhere.</span>{' '}
            Automatically.
          </h1>
          <p style={{ fontSize: 'clamp(15px,1.6vw,18px)', color: '#94a3b8', lineHeight: '1.75', maxWidth: '620px', margin: '0 auto 40px' }}>
            Vantage is the AI powered content automation tool that publishes your videos to YouTube, Facebook, TikTok and Instagram simultaneously — with platform optimised titles, descriptions, keywords and thumbnails generated automatically.
          </p>
          <div className="hero-btns" style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '60px' }}>
            <button className="btn-hover" onClick={onSignup} style={{ padding: '16px 40px', borderRadius: '12px', cursor: 'pointer', background: 'linear-gradient(135deg,#00c853,#1de9b6)', border: 'none', color: '#000', fontSize: '16px', fontWeight: '800', fontFamily: 'inherit' }}>Start Free Trial →</button>
            <button className="btn-secondary-hover" onClick={() => scrollTo(pricingRef)} style={{ padding: '16px 40px', borderRadius: '12px', cursor: 'pointer', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: '16px', fontWeight: '700', fontFamily: 'inherit' }}>View Pricing</button>
          </div>
          <div className="stats-row" style={{ display: 'flex', gap: '48px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {[{ number: '4', label: 'Platforms Supported' }, { number: '100%', label: 'Automatic Publishing' }, { number: '5x', label: 'Faster Content Creation' }].map((stat, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: '900', color: '#00c853', letterSpacing: '-1px' }}>{stat.number}</div>
                <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500', marginTop: '6px' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO VIDEO */}
      <section style={{ padding: '80px 40px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{ fontSize: 'clamp(24px,3vw,40px)', fontWeight: '800', marginBottom: '16px', letterSpacing: '-0.3px' }}>See Vantage in Action</h2>
            <p style={{ color: '#64748b', fontSize: '16px' }}>Watch how Vantage automates your entire content workflow in minutes</p>
          </div>
          <div style={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(0,200,83,0.2)', background: 'rgba(0,200,83,0.03)', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: 'pointer' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,200,83,0.05), rgba(0,188,212,0.05))' }} />
            <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg,#00c853,#1de9b6)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '32px' }}>▶</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#fff' }}>Demo Video Coming Soon</div>
              <div style={{ fontSize: '14px', color: '#64748b', marginTop: '8px' }}>Full walkthrough of the Vantage platform</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES — 3 + 3 + 2 centered */}
      <section ref={featuresRef} style={{ padding: '80px 40px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '20px', marginBottom: '16px', background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.3)', fontSize: '13px', fontWeight: '600', color: '#00c853' }}>✨ Powered by Obsidian AI</div>
            <h2 style={{ fontSize: 'clamp(24px,3vw,40px)', fontWeight: '800', marginBottom: '16px', letterSpacing: '-0.3px' }}>
              Everything You Need to{' '}
              <span style={{ background: 'linear-gradient(135deg,#00c853,#1de9b6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Dominate</span>
            </h2>
            <p style={{ color: '#64748b', fontSize: '16px', maxWidth: '600px', margin: '0 auto' }}>Vantage combines AI content generation, automatic publishing and performance analytics in one powerful platform</p>
          </div>
          {/* Row 1 — 3 cards */}
          <div className="features-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '20px', marginBottom: '20px' }}>
            {FEATURES.slice(0, 3).map((f, i) => <FeatureCard key={i} feature={f} />)}
          </div>
          {/* Row 2 — 3 cards */}
          <div className="features-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '20px', marginBottom: '20px' }}>
            {FEATURES.slice(3, 6).map((f, i) => <FeatureCard key={i} feature={f} />)}
          </div>
          {/* Row 3 — 2 cards centered */}
          <div className="features-row-last" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '20px', width: '66.6%', margin: '0 auto' }}>
            {FEATURES.slice(6).map((f, i) => <FeatureCard key={i} feature={f} />)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ padding: '80px 40px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ fontSize: 'clamp(24px,3vw,40px)', fontWeight: '800', marginBottom: '16px', letterSpacing: '-0.3px' }}>How It Works</h2>
            <p style={{ color: '#64748b', fontSize: '16px' }}>Three steps to fully automated content publishing</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {[
              { step: '01', title: 'Upload Your Video', desc: 'Select your video from your computer and choose which platforms and channels to publish to. Add any keywords or hashtags you want included.', color: '#00c853' },
              { step: '02', title: 'Obsidian AI Generates Everything', desc: 'Our AI automatically detects your niche, generates 5 platform specific options for titles, descriptions, keywords and hashtags — each optimised for that platform algorithm. You pick one or let Obsidian decide.', color: '#00bcd4' },
              { step: '03', title: 'Publish Automatically', desc: 'Set a schedule or publish instantly. Vantage handles everything — uploading the video, applying metadata and publishing to all platforms simultaneously. Zero human intervention needed.', color: '#7c4dff' }
            ].map((item, i) => (
              <div key={i} className="step-row" style={{ display: 'flex', gap: '32px', alignItems: 'center', padding: '32px 36px', borderRadius: '16px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '64px', fontWeight: '900', color: item.color, opacity: 0.2, lineHeight: 1, flexShrink: 0, letterSpacing: '-2px', minWidth: '80px', textAlign: 'center' }}>{item.step}</div>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '10px', color: item.color, letterSpacing: '-0.2px' }}>{item.title}</h3>
                  <p style={{ fontSize: '15px', color: '#64748b', lineHeight: '1.75' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING — 3 top + 2 bottom centered */}
      <section ref={pricingRef} style={{ padding: '80px 40px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{ fontSize: 'clamp(24px,3vw,40px)', fontWeight: '800', marginBottom: '16px', letterSpacing: '-0.3px' }}>Simple, Transparent Pricing</h2>
            <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '24px' }}>Choose the plan that fits your content creation needs</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {Object.keys(CURRENCIES).map(cur => (
                <button key={cur} onClick={() => setCurrency(cur)} style={{ padding: '6px 16px', borderRadius: '20px', cursor: 'pointer', background: currency === cur ? 'rgba(0,200,83,0.2)' : 'rgba(255,255,255,0.05)', border: '1px solid ' + (currency === cur ? 'rgba(0,200,83,0.5)' : 'rgba(255,255,255,0.1)'), color: currency === cur ? '#00c853' : '#64748b', fontSize: '13px', fontWeight: '600', transition: 'all 0.2s', fontFamily: 'inherit' }}>{cur}</button>
              ))}
            </div>
          </div>
          {/* Top 3 */}
          <div className="pricing-grid-top" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '20px', marginBottom: '20px' }}>
            {PLANS.slice(0, 3).map((plan, i) => <PlanCard key={i} plan={plan} />)}
          </div>
          {/* Bottom 2 centered */}
          <div className="pricing-grid-bot" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '20px', width: '66.6%', margin: '0 auto' }}>
            {PLANS.slice(3).map((plan, i) => <PlanCard key={i} plan={plan} />)}
          </div>
        </div>
      </section>

      {/* PAYMENT MODAL */}
      {showPayment && selectedPlan && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#0f1629', borderRadius: '20px', padding: '40px', maxWidth: '500px', width: '100%', border: '1px solid rgba(0,200,83,0.3)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '24px', fontWeight: '800' }}>{selectedPlan.icon} {selectedPlan.name} Plan</h3>
              <button onClick={() => setShowPayment(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px', fontFamily: 'inherit' }}>×</button>
            </div>
            <div style={{ padding: '20px', borderRadius: '12px', background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.2)', marginBottom: '24px' }}>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Monthly Price</div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#00c853' }}>{formatPrice(selectedPlan.pkr)}<span style={{ fontSize: '14px', color: '#64748b', fontWeight: '400' }}>/month</span></div>
              <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>PKR {selectedPlan.pkr.toLocaleString()} /month</div>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '16px', color: '#fff' }}>How to Pay:</div>
              {[{ method: '💚 JazzCash', number: '03114608845', name: 'Vantage Studio' }, { method: '🟣 EasyPaisa', number: '03114608845', name: 'Vantage Studio' }].map((payment, i) => (
                <div key={i} style={{ padding: '16px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '12px' }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '8px' }}>{payment.method}</div>
                  <div style={{ fontSize: '13px', color: '#94a3b8' }}>Account Number: <span style={{ color: '#00c853', fontWeight: '700' }}>{payment.number}</span></div>
                  <div style={{ fontSize: '13px', color: '#94a3b8' }}>Account Name: <span style={{ color: '#fff' }}>{payment.name}</span></div>
                </div>
              ))}
            </div>
            <div style={{ padding: '16px', borderRadius: '10px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: '24px', fontSize: '13px', color: '#f59e0b', lineHeight: '1.6' }}>
              <strong>After payment:</strong> Send your payment screenshot to <strong>support@vantagestudioai.com</strong> or WhatsApp <strong>03114608845</strong> with your email address. We will activate your account within 24 hours.
            </div>
            <button className="btn-hover" onClick={onSignup} style={{ width: '100%', padding: '14px', borderRadius: '10px', background: 'linear-gradient(135deg,#00c853,#1de9b6)', border: 'none', color: '#000', fontSize: '15px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' }}>
              Create Account to Get Started →
            </button>
          </div>
        </div>
      )}

      {/* CONTACT */}
      <section ref={contactRef} style={{ padding: '80px 40px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(24px,3vw,40px)', fontWeight: '800', marginBottom: '16px', letterSpacing: '-0.3px' }}>Get in Touch</h2>
          <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '40px' }}>Have questions? We are here to help you get started</p>
          <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '20px' }}>
            {[
              { icon: '📧', label: 'Email', value: 'support@vantagestudioai.com', link: 'mailto:support@vantagestudioai.com' },
              { icon: '📱', label: 'WhatsApp', value: '03114608845', link: 'https://wa.me/923114608845' },
              { icon: '🌐', label: 'Website', value: 'vantagestudioai.com', link: 'https://vantagestudioai.com' }
            ].map((contact, i) => (
              <a key={i} href={contact.link} target="_blank" rel="noopener noreferrer" style={{ padding: '24px 16px', borderRadius: '16px', textDecoration: 'none', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', display: 'block', transition: 'all 0.3s ease' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,200,83,0.06)'; e.currentTarget.style.borderColor = 'rgba(0,200,83,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>{contact.icon}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px', fontWeight: '600' }}>{contact.label}</div>
                <div style={{ fontSize: '13px', color: '#00c853', fontWeight: '600', wordBreak: 'break-word', overflowWrap: 'break-word', lineHeight: '1.5' }}>{contact.value}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: '36px 40px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="footer-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/logo.png" alt="Vantage" style={{ width: '28px', height: '28px', borderRadius: '8px', objectFit: 'cover' }}
              onError={e => { e.target.style.display = 'none'; }} />
            <span style={{ fontSize: '16px', fontWeight: '800', color: '#fff', letterSpacing: '-0.2px' }}>Vantage</span>
          </div>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <span onClick={onPrivacy} style={{ fontSize: '13px', color: '#64748b', cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color = '#00c853'} onMouseLeave={e => e.target.style.color = '#64748b'}>Privacy Policy</span>
            <span onClick={onTerms} style={{ fontSize: '13px', color: '#64748b', cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color = '#00c853'} onMouseLeave={e => e.target.style.color = '#64748b'}>Terms of Service</span>
            <span onClick={onReturn} style={{ fontSize: '13px', color: '#64748b', cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color = '#00c853'} onMouseLeave={e => e.target.style.color = '#64748b'}>Return Policy</span>
          </div>
          <div style={{ fontSize: '13px', color: '#334155' }}>© 2026 Vantage Studio AI. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}